import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, CheckCircle2, Copy, MessageCircle, Search, Paperclip, Trash2 } from 'lucide-react';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { registrarPassoRetencao } from '../../src/lib/retencaoCancelamento';
import { dispararAvisoProtocolo } from '../../src/lib/envioCancelamento';
import {
    enderecoExibivel,
    prepararUcsDoPedido, mensagemListaVazia, detalheObrigatorio, MOTIVOS_PORTAL,
    abrirPedidoCancelamentoPortal, listarMeusCancelamentos,
} from '../../src/lib/cancelamentoPortal';
import type {
    UcParaPedido, MotivoPortal, MeuCancelamento, ResultadoPedidoPortal,
} from '../../src/lib/cancelamentoPortal';
import {
    motivoArquivoRecusado, enviarDocumentoCancelamento, meusDocumentosDoPedido, VINCULOS_SOLICITANTE,
    LIMITE_ARQUIVOS_POR_PEDIDO,
} from '../../src/lib/documentosCancelamento';
import type { VinculoSolicitante } from '../../src/lib/documentosCancelamento';
import PortalConsumidorHeader from './PortalConsumidorHeader';
import { useMarcaPortal, useTituloPortal } from './marca/MarcaPortalContext';
import { linkWhatsapp } from './marca/marcaPortal';

/**
 * Pedido de cancelamento pelo Portal (#5833). Entrada discreta, dentro da Ajuda.
 *
 * Nada é gravado pelo navegador: a criação vai por `abrir_cancelamento_portal`
 * (SECURITY DEFINER), que confere no servidor se a unidade é mesmo do cliente —
 * a tabela de cancelamentos está fechada para o consumidor desde a demanda 02.
 *
 * O Portal não tem dark mode, por isso as classes aqui são só do tema claro,
 * como no resto das telas do Portal.
 */
const fmtData = (v: string | null | undefined): string => {
    const t = String(v ?? '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '—';
    const [ano, mes, dia] = t.split('-');
    return `${dia}/${mes}/${ano}`;
};

const STATUS_LABEL: Record<string, string> = {
    aberta: 'Recebido',
    em_andamento: 'Em análise',
    concluida: 'Concluído',
    cancelada: 'Encerrado a seu pedido',
};

const PortalConsumidorCancelamento: React.FC = () => {
    const { user, profile, loading, signOut } = useAuth();
    const location = useLocation();
    const { addToast } = useToast();

    const [carregando, setCarregando] = useState(true);
    const [erroCarga, setErroCarga] = useState<string | null>(null);
    const [ucs, setUcs] = useState<UcParaPedido[]>([]);
    const [meus, setMeus] = useState<MeuCancelamento[]>([]);

    const [buscaUc, setBuscaUc] = useState('');
    const [ucSelecionada, setUcSelecionada] = useState<number | null>(null);
    const [motivo, setMotivo] = useState<MotivoPortal>('insatisfacao');
    const [detalhe, setDetalhe] = useState('');
    // Quem está pedindo e os documentos (#5828).
    const [vinculo, setVinculo] = useState<VinculoSolicitante>('titular');
    const [solicitanteNome, setSolicitanteNome] = useState('');
    const [solicitanteCpf, setSolicitanteCpf] = useState('');
    const [solicitanteEmail, setSolicitanteEmail] = useState('');
    const [solicitanteTelefone, setSolicitanteTelefone] = useState('');
    const [declara, setDeclara] = useState(false);
    const [arquivos, setArquivos] = useState<File[]>([]);
    const [erroArquivo, setErroArquivo] = useState<string | null>(null);
    const [enviandoDocs, setEnviandoDocs] = useState(false);
    const [confirmando, setConfirmando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [resultado, setResultado] = useState<ResultadoPedidoPortal | null>(null);
    // Documentos que ficaram sem subir (falha de rede no meio do envio): o
    // pedido JÁ existe, então a tela final precisa deixar completar em vez de
    // dizer que deu erro e perder os anexos.
    const [pendentes, setPendentes] = useState<File[]>([]);
    const [docsDoPedido, setDocsDoPedido] = useState<Array<{ id: number; nome_original: string }>>([]);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErroCarga(null);
        try {
            // A RLS do Portal já limita as UCs às do CPF logado (demanda 02 e anteriores).
            const [{ data: ucsData, error: ucsErro }, pedidos] = await Promise.all([
                supabase.from('unidades_consumidoras')
                    .select('id, numero_uc, endereco, situacao, empresa_parceira_id')
                    .order('numero_uc'),
                listarMeusCancelamentos(),
            ]);
            if (ucsErro) throw ucsErro;
            setMeus(pedidos);
            setUcs(prepararUcsDoPedido((ucsData || []) as any, pedidos.map(p => ({
                uc_id: p.uc_id, protocolo: p.protocolo, status: p.status,
                data_solicitacao: p.data_solicitacao, data_retorno_prevista: p.data_retorno_prevista,
            }))));
        } catch (e: any) {
            console.error('Erro ao carregar o pedido de cancelamento', e);
            setErroCarga('Não conseguimos carregar suas unidades agora. Tente de novo em alguns minutos.');
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => {
        // "Sou o titular" preenche com o cadastro; nos outros vínculos o cliente
        // escreve quem é, porque não é o titular da conta.
        if (vinculo === 'titular') {
            setSolicitanteNome(profile?.full_name || '');
            setSolicitanteCpf(profile?.cpf_cnpj || '');
        }
    }, [vinculo, profile]);

    useEffect(() => {
        // Quem digita a URL do formulário direto não passa pela retenção (#5832):
        // sem este registro, a medição contaria só quem veio pela Ajuda e
        // subestimaria o total de pedidos.
        if (!(location.state as { veioDaRetencao?: boolean } | null)?.veioDaRetencao) {
            registrarPassoRetencao('seguiu', null, null);
        }
    }, [location.state]);

    const { marca } = useMarcaPortal();

    useTituloPortal('Cancelar assinatura');

    useEffect(() => {
        carregar();
    }, [carregar]);

    /** O que falta preencher, na ordem da tela. Null quando está tudo certo. */
    const faltaPreencher = (): string | null => {
        if (detalheObrigatorio(motivo) && !detalhe.trim()) return 'Conte rapidamente o motivo para a gente entender seu pedido.';
        if (!solicitanteNome.trim()) return 'Diga quem está fazendo o pedido.';
        if (arquivos.length === 0) return 'Anexe ao menos um documento que comprove o pedido.';
        if (!declara) return 'Confirme que as informações são verdadeiras para seguir.';
        return null;
    };

    /** Só abre a confirmação com tudo preenchido: evita o cliente confirmar e voltar. */
    const revisar = () => {
        const falta = faltaPreencher();
        if (falta) { addToast(falta, 'warning'); return; }
        setConfirmando(true);
    };

    const enviar = async () => {
        if (!ucSelecionada) return;
        const falta = faltaPreencher();
        if (falta) { addToast(falta, 'warning'); return; }
        setEnviando(true);
        try {
            const r = await abrirPedidoCancelamentoPortal(ucSelecionada, motivo, detalhe, {
                nome: solicitanteNome, cpf: solicitanteCpf, email: solicitanteEmail,
                telefone: solicitanteTelefone, vinculo, declaraVeracidade: declara,
            });
            // Anexos só depois do pedido existir: é o id dele que forma a pasta.
            // Vale também quando o pedido já existia — os documentos que o
            // cliente acabou de escolher continuam valendo para aquele pedido.
            setResultado(r);
            setConfirmando(false);
            if (user?.id && arquivos.length > 0) {
                setEnviandoDocs(true);
                const naoEnviados = await enviarDocumentos(r.id, user.id, arquivos);
                setEnviandoDocs(false);
                setPendentes(naoEnviados);
                if (naoEnviados.length > 0) {
                    addToast(`Seu pedido foi registrado, mas ${naoEnviados.length} documento(s) não subiram. Tente enviar de novo abaixo.`, 'warning');
                }
            }
            await atualizarDocumentosDoPedido(r.id);
            // Aviso de protocolo (#5830). Hoje não faz nada: a chave
            // `cancelamento_envio_modo` está 'desligado' e a função só dispara
            // quando alguém ligar. Nunca lança — o pedido já está registrado e a
            // tela do protocolo não pode depender de a mensagem sair.
            dispararAvisoProtocolo({
                solicitacaoId: r.id,
                clienteNome: profile?.full_name ?? null,
                protocolo: r.protocolo ?? null,
                ucNumero: ucs.find(u => u.id === ucSelecionada)?.numero_uc ?? null,
                dataSolicitacao: r.data_solicitacao ?? null,
                dataRetornoPrevista: r.data_retorno_prevista ?? null,
            });
            carregar();
        } catch (e: any) {
            console.error('Erro ao abrir o pedido de cancelamento', e);
            addToast(e?.message || 'Não conseguimos registrar seu pedido agora. Tente de novo.', 'error');
        } finally {
            setEnviando(false);
            setEnviandoDocs(false);
        }
    };

    /** Envia um a um e devolve os que não subiram, para o cliente tentar de novo. */
    const enviarDocumentos = async (solicitacaoId: number, usuarioId: string, lista: File[]): Promise<File[]> => {
        const falharam: File[] = [];
        let enviados = docsDoPedido.length;
        for (const arquivo of lista) {
            try {
                await enviarDocumentoCancelamento(solicitacaoId, usuarioId, arquivo, { peloPortal: true, jaEnviados: enviados });
                enviados += 1;
            } catch (e) {
                console.error('Documento não enviado', arquivo.name, e);
                falharam.push(arquivo);
            }
        }
        return falharam;
    };

    const atualizarDocumentosDoPedido = async (solicitacaoId: number) => {
        try {
            setDocsDoPedido(await meusDocumentosDoPedido(solicitacaoId));
        } catch (e) {
            console.error('Não foi possível listar os documentos do pedido', e);
        }
    };

    const reenviarPendentes = async () => {
        if (!resultado || !user?.id || pendentes.length === 0) return;
        setEnviandoDocs(true);
        const falharam = await enviarDocumentos(resultado.id, user.id, pendentes);
        setPendentes(falharam);
        await atualizarDocumentosDoPedido(resultado.id);
        setEnviandoDocs(false);
        addToast(falharam.length === 0 ? 'Documentos enviados.' : `${falharam.length} documento(s) ainda não subiram.`,
            falharam.length === 0 ? 'success' : 'warning');
    };

    const adicionarArquivos = (lista: FileList | null) => {
        setErroArquivo(null);
        const novos: File[] = [];
        for (const arquivo of Array.from(lista || [])) {
            const recusa = motivoArquivoRecusado(arquivo, arquivos.length + novos.length);
            if (recusa) { setErroArquivo(recusa); break; }
            novos.push(arquivo);
        }
        if (novos.length > 0) setArquivos(atual => [...atual, ...novos]);
    };

    const copiarProtocolo = async (protocolo: string) => {
        try {
            await navigator.clipboard.writeText(protocolo);
            addToast('Protocolo copiado.', 'success');
        } catch {
            addToast('Não foi possível copiar. Anote o número do protocolo.', 'warning');
        }
    };

    if (loading || carregando) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    const disponiveis = ucs.filter(u => u.podePedir);
    const vazio = mensagemListaVazia(ucs.length, disponiveis.length);
    const ucEscolhida = ucs.find(u => u.id === ucSelecionada) || null;
    // Com muitas unidades na conta, rolar tudo pra achar uma vira obstáculo —
    // a busca só aparece quando compensa (poucas unidades, sem por quê).
    const termoBusca = buscaUc.trim().toLowerCase();
    const ucsFiltradas = termoBusca
        ? ucs.filter(u =>
            (u.numero_uc || '').toLowerCase().includes(termoBusca)
            || (u.endereco || '').toLowerCase().includes(termoBusca))
        : ucs;

    return (
        <div className="min-h-screen bg-gray-50">
            <PortalConsumidorHeader />

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
                <div className="flex items-center gap-3">
                    <Link to="/portal-consumidor/ajuda" aria-label="Voltar para a Ajuda"
                        className="p-2 rounded-full text-gray-400 hover:text-primary hover:bg-gray-100 transition-colors">
                        <ArrowLeft size={20} />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Cancelar minha adesão</h1>
                        <p className="text-sm text-gray-500">Registre o pedido e acompanhe pelo número de protocolo.</p>
                    </div>
                </div>

                {resultado ? (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center space-y-3">
                        <CheckCircle2 size={36} className="text-primary mx-auto" aria-hidden="true" />
                        <h2 className="text-lg font-bold text-gray-900">
                            {resultado.ja_existia ? 'Você já tinha um pedido em andamento' : 'Pedido registrado'}
                        </h2>
                        <div className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2">
                            <span className="font-mono text-sm font-bold text-gray-800 select-all">{resultado.protocolo || '—'}</span>
                            {resultado.protocolo && (
                                <button onClick={() => copiarProtocolo(resultado.protocolo!)} aria-label="Copiar protocolo"
                                    className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                                    <Copy size={14} />
                                </button>
                            )}
                        </div>
                        <p className="text-sm text-gray-600">
                            Nossa equipe vai analisar e responder até {fmtData(resultado.data_retorno_prevista)}.
                        </p>

                        {docsDoPedido.length > 0 && (
                            <p className="text-xs text-gray-500">
                                {docsDoPedido.length} documento(s) recebido(s): {docsDoPedido.map(d => d.nome_original).join(', ')}
                            </p>
                        )}

                        {pendentes.length > 0 && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-left space-y-2">
                                <p className="text-xs text-gray-700">
                                    {pendentes.length} documento(s) não foram enviados: {pendentes.map(a => a.name).join(', ')}.
                                    Seu pedido já está registrado — é só tentar enviar de novo.
                                </p>
                                <button type="button" onClick={reenviarPendentes} disabled={enviandoDocs}
                                    className="inline-flex items-center justify-center gap-2 py-2 px-4 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50">
                                    {enviandoDocs ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
                                    {enviandoDocs ? 'Enviando...' : 'Enviar novamente'}
                                </button>
                            </div>
                        )}
                        <div className="pt-2">
                            <Link to="/portal-consumidor/inicio" className="inline-flex items-center justify-center gap-2 py-2.5 px-5 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-colors">
                                Voltar para o início
                            </Link>
                        </div>
                    </div>
                ) : erroCarga ? (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center space-y-3">
                        <AlertTriangle size={28} className="text-amber-500 mx-auto" aria-hidden="true" />
                        <p className="text-sm text-gray-700">{erroCarga}</p>
                        <button onClick={carregar} className="text-sm font-bold text-primary hover:opacity-80">Tentar de novo</button>
                    </div>
                ) : (
                    <>
                        {meus.length > 0 && (
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                                <h2 className="text-sm font-bold text-gray-900 mb-3">Seus pedidos</h2>
                                <div className="flex flex-col gap-2">
                                    {meus.map(p => (
                                        <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-2.5">
                                            <div className="min-w-0">
                                                <p className="font-mono text-sm font-semibold text-gray-800">{p.protocolo || `Pedido #${p.id}`}</p>
                                                <p className="text-xs text-gray-500">
                                                    Unidade {p.uc_numero} · pedido em {fmtData(p.data_solicitacao)}
                                                    {p.data_retorno_prevista ? ` · retorno até ${fmtData(p.data_retorno_prevista)}` : ''}
                                                </p>
                                            </div>
                                            <span className="text-[11px] font-bold px-2 py-1 rounded-md border bg-white text-gray-600 border-gray-200">
                                                {STATUS_LABEL[p.status] || p.status}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
                            <div>
                                <h2 className="text-sm font-bold text-gray-900 mb-1">Qual unidade você quer cancelar?</h2>
                                <p className="text-xs text-gray-500">Um pedido por unidade. Se tiver mais de uma, repita depois para as outras.</p>
                            </div>

                            {vazio ? (
                                <p className="text-sm text-gray-500">{vazio}</p>
                            ) : null}

                            {ucs.length > 8 && (
                                <div className="relative">
                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                                    <input
                                        type="text"
                                        value={buscaUc}
                                        onChange={e => setBuscaUc(e.target.value)}
                                        placeholder="Buscar pelo número da unidade ou endereço"
                                        aria-label="Buscar unidade"
                                        className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2.5 text-base sm:text-sm bg-white outline-none focus:border-primary"
                                    />
                                </div>
                            )}

                            {ucs.length > 8 && ucsFiltradas.length === 0 && (
                                <p className="text-sm text-gray-500">Nenhuma unidade encontrada com esse termo.</p>
                            )}

                            <div className="flex flex-col gap-2 max-h-[420px] overflow-y-auto pr-1">
                                {ucsFiltradas.map(u => (
                                    <label key={u.id}
                                        className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${u.podePedir
                                            ? `cursor-pointer ${ucSelecionada === u.id ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50'}`
                                            : 'border-gray-100 bg-gray-50/60'}`}>
                                        <input
                                            type="radio"
                                            name="uc-cancelamento"
                                            className="mt-1"
                                            disabled={!u.podePedir}
                                            checked={ucSelecionada === u.id}
                                            onChange={() => setUcSelecionada(u.id)}
                                        />
                                        <span className="min-w-0">
                                            <span className="block text-sm font-semibold text-gray-800">Unidade {u.numero_uc}</span>
                                            {enderecoExibivel(u.endereco) ? <span className="block text-xs text-gray-500">{enderecoExibivel(u.endereco)}</span> : null}
                                            {u.aviso ? <span className="block text-xs text-amber-700 mt-1">{u.aviso}</span> : null}
                                        </span>
                                    </label>
                                ))}
                            </div>

                            {ucSelecionada !== null && (
                                <div className="space-y-3 pt-2 border-t border-gray-100">
                                    <div>
                                        <label htmlFor="motivo-cancelamento" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                            Por que você quer cancelar?
                                        </label>
                                        <select id="motivo-cancelamento" value={motivo}
                                            onChange={e => setMotivo(e.target.value as MotivoPortal)}
                                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-base sm:text-sm bg-white outline-none focus:border-primary">
                                            {MOTIVOS_PORTAL.map(m => <option key={m.valor} value={m.valor}>{m.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="detalhe-cancelamento" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                            Quer contar mais? {detalheObrigatorio(motivo) ? '(obrigatório)' : '(opcional)'}
                                        </label>
                                        <textarea id="detalhe-cancelamento" value={detalhe} rows={3}
                                            onChange={e => setDetalhe(e.target.value)}
                                            placeholder="Escreva aqui o que motivou seu pedido."
                                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-base sm:text-sm bg-white outline-none focus:border-primary resize-y" />
                                    </div>

                                    <div className="pt-2 border-t border-gray-100 space-y-3">
                                        <div>
                                            <label htmlFor="vinculo-solicitante" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                                Quem está pedindo?
                                            </label>
                                            <select id="vinculo-solicitante" value={vinculo}
                                                onChange={e => setVinculo(e.target.value as VinculoSolicitante)}
                                                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-base sm:text-sm bg-white outline-none focus:border-primary">
                                                {VINCULOS_SOLICITANTE.map(v => <option key={v.valor} value={v.valor}>{v.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <label htmlFor="solicitante-nome" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Nome completo</label>
                                                <input id="solicitante-nome" value={solicitanteNome} onChange={e => setSolicitanteNome(e.target.value)}
                                                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-base sm:text-sm bg-white outline-none focus:border-primary" />
                                            </div>
                                            <div>
                                                <label htmlFor="solicitante-cpf" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">CPF</label>
                                                <input id="solicitante-cpf" value={solicitanteCpf} onChange={e => setSolicitanteCpf(e.target.value)} inputMode="numeric"
                                                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-base sm:text-sm bg-white outline-none focus:border-primary" />
                                            </div>
                                            <div>
                                                <label htmlFor="solicitante-email" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">E-mail</label>
                                                <input id="solicitante-email" value={solicitanteEmail} onChange={e => setSolicitanteEmail(e.target.value)} type="email"
                                                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-base sm:text-sm bg-white outline-none focus:border-primary" />
                                            </div>
                                            <div>
                                                <label htmlFor="solicitante-telefone" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Telefone</label>
                                                <input id="solicitante-telefone" value={solicitanteTelefone} onChange={e => setSolicitanteTelefone(e.target.value)} inputMode="tel"
                                                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-base sm:text-sm bg-white outline-none focus:border-primary" />
                                            </div>
                                        </div>

                                        <div>
                                            <p className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                                Documentos ({arquivos.length} de {LIMITE_ARQUIVOS_POR_PEDIDO})
                                            </p>
                                            <p className="text-xs text-gray-500 mb-2">
                                                Anexe {VINCULOS_SOLICITANTE.find(v => v.valor === vinculo)?.pedeDocumento}. PDF ou foto, até 20 MB cada.
                                            </p>
                                            <label className="inline-flex items-center gap-2 py-2 px-4 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors">
                                                <Paperclip size={15} /> Escolher arquivo
                                                <input type="file" multiple className="hidden"
                                                    accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                                                    onChange={e => { adicionarArquivos(e.target.files); e.target.value = ''; }} />
                                            </label>
                                            {erroArquivo ? <p className="text-xs text-red-600 mt-1.5">{erroArquivo}</p> : null}
                                            {arquivos.length > 0 && (
                                                <ul className="mt-2 flex flex-col gap-1.5">
                                                    {arquivos.map((a, i) => (
                                                        <li key={`${a.name}-${i}`} className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2">
                                                            <span className="text-xs text-gray-700 truncate">{a.name}</span>
                                                            <button type="button" aria-label={`Remover ${a.name}`}
                                                                onClick={() => setArquivos(atual => atual.filter((_, idx) => idx !== i))}
                                                                className="p-1 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>

                                        <label className="flex items-start gap-2 text-xs text-gray-600">
                                            <input type="checkbox" checked={declara} onChange={e => setDeclara(e.target.checked)} className="mt-0.5" />
                                            <span>Declaro que as informações e os documentos enviados são verdadeiros e que tenho poderes para pedir o cancelamento desta unidade.</span>
                                        </label>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-3 pt-1">
                                        <a href={linkWhatsapp(marca, 'Olá! Quero falar sobre minha assinatura antes de cancelar.')}
                                            target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center justify-center gap-2 py-2.5 px-5 rounded-xl text-sm font-bold text-primary bg-white border border-primary/20 hover:bg-primary/5 transition-colors">
                                            <MessageCircle size={16} /> {marca.chave === 'vigor' ? 'Falar com a Vigor antes' : 'Falar com o atendimento antes'}
                                        </a>
                                        <button type="button" onClick={revisar}
                                            className="inline-flex items-center justify-center gap-2 py-2.5 px-5 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-colors">
                                            Continuar com o cancelamento
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {confirmando && ucEscolhida && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                        role="dialog" aria-modal="true" aria-labelledby="confirmar-cancelamento-titulo">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
                            <h2 id="confirmar-cancelamento-titulo" className="text-lg font-bold text-gray-900">Confirmar o pedido?</h2>
                            <p className="text-sm text-gray-600">
                                Vamos registrar o cancelamento da unidade <strong>{ucEscolhida.numero_uc}</strong> e você
                                recebe um número de protocolo para acompanhar. Nossa equipe entra em contato com o retorno da análise.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-end">
                                <button type="button" onClick={() => setConfirmando(false)} disabled={enviando}
                                    className="py-2.5 px-5 rounded-xl text-sm font-bold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50">
                                    Voltar
                                </button>
                                <button type="button" onClick={enviar} disabled={enviando}
                                    className="inline-flex items-center justify-center gap-2 py-2.5 px-5 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50">
                                    {enviando ? <Loader2 size={16} className="animate-spin" /> : null}
                                    {enviando ? (enviandoDocs ? 'Enviando documentos...' : 'Registrando...') : 'Confirmar pedido'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default PortalConsumidorCancelamento;
